import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import BottomNav from './BottomNav';
import FloatingTimer from './FloatingTimer';
import OfflineBanner from './OfflineBanner';
import Avatar from './Avatar';
import BabySelector from '../../features/babies/BabySelector';
import PlanBadge from '../../features/subscription/PlanBadge';
import SleepSuiteServices from '../../features/sleep/SleepSuiteServices';
import { useLegacySleepTimerMigration } from '../../features/sleep/useOpenSleep';
import usePendingInvite from '../../features/access/usePendingInvite';
import { usePendingPass } from '../../features/referral/usePendingPass';
import { useLapsedRedirect } from '../../features/subscription/useLapsedRedirect';
import { useSecondAsk } from '../../features/subscription/useSecondAsk';
import { useFirstAsk } from '../../features/subscription/useFirstAsk';
import { useTrialOfferLifecycle } from '../../features/offer/trialOffer';
import { useValidatedBabyId } from '../hooks/useValidatedBabyId';
import { getBabyAgeText } from '../utils/milestoneHelper';
import { FOCUS_RING, cx } from './toneStyles';
import {
  MdExpandMore,
  MdAdd,
  MdHome,
  MdRestaurant,
  MdWaterDrop,
  MdBedtime,
  MdBabyChangingStation,
  MdToys,
  MdThermostat,
  MdStraighten,
  MdBarChart,
  MdLightbulb,
  MdShield,
  MdSettings,
} from '../icons';

const SIDEBAR_LINKS = [
  { to: '/', icon: MdHome, label: 'Dashboard' },
  { to: '/feed', icon: MdRestaurant, label: 'Feeding' },
  { to: '/pumping', icon: MdWaterDrop, label: 'Pumping' },
  { to: '/sleep', icon: MdBedtime, label: 'Sleep' },
  { to: '/nappy', icon: MdBabyChangingStation, label: 'Nappy' },
  { to: '/activity', icon: MdToys, label: 'Activity' },
  { to: '/illness', icon: MdThermostat, label: 'Illness' },
  { to: '/growth', icon: MdStraighten, label: 'Growth' },
  { to: '/reports', icon: MdBarChart, label: 'Reports' },
  { to: '/tips', icon: MdLightbulb, label: 'Tips' },
  { to: '/safety', icon: MdShield, label: 'Safety' },
  { to: '/settings', icon: MdSettings, label: 'Settings' },
];

export default function Layout() {
  // Mount once at the layout level so the hook's effect repairs a stale
  // selectedBabyId in the zustand store; every page reading the store benefits.
  const { babies, selectedBabyId, isFetched } = useValidatedBabyId();
  // One-off upgrade path: a zustand SLEEP timer left running by v1.2.x becomes
  // a real open SLEEP event. Mounted exactly once, here.
  useLegacySleepTimerMigration();
  // Redeems a caregiver invitation the user opened before they had an account.
  // Mounted here so it fires however they get back into the app.
  usePendingInvite();
  // v1.5.0, each mounted exactly once in the authenticated shell: redeems a
  // referral pass code stashed before sign-in; sends a lapsed subscriber,
  // ended trial, pass or preview to the paywall once; cancels the pending
  // trial-offer notifications the moment the plan turns paid.
  usePendingPass();
  useLapsedRedirect();
  // v1.5.1: from day 8, a Free account that never subscribed sees the plans once more.
  useSecondAsk();
  // v1.5.3: the first ask, once the baby has a day of entries
  useFirstAsk();
  useTrialOfferLifecycle();

  const [switcherOpen, setSwitcherOpen] = useState(false);

  return (
    <div className="min-h-screen bg-surface-50 dark:bg-night-bg pb-[calc(5rem+env(safe-area-inset-bottom))] md:pb-0 md:pl-64">
      {/* Single subscriber driving local notifications, Live Activity and
          widget data from the shared sleep-schedule query. Mounted here (the
          authenticated app shell) exactly once — renders nothing. */}
      <SleepSuiteServices />

      {/* ONE element owns the notch inset. `env(safe-area-inset-top)` is static
          padding — it does not disappear when something else is above you — so
          an offline strip with its own `safe-area-top` plus a `safe-area-top`
          header inset the notch twice and pushed the whole app down. The strip
          renders inside this box instead, above the header row. */}
      <div className="sticky top-0 z-30 bg-white dark:bg-night-surface border-b border-surface-200 dark:border-night-border safe-area-top">
        <OfflineBanner variant="shell" />
        <header className="px-4 py-2 flex items-center justify-between gap-2">
          <BabyChip
            babies={babies}
            selectedBabyId={selectedBabyId}
            isFetched={isFetched}
            onOpenSwitcher={() => setSwitcherOpen(true)}
          />
          {/* No `overflow-hidden` here: both pills reach their 44 px hit box
              through a `before:` pseudo element, and clipping removes that from
              the paint AND the hit-test region (leaving a 22–28 px target).
              `min-w-0` plus the pills' own truncation already contain them. */}
          <div className="flex items-center gap-1.5 min-w-0">
            <FloatingTimer />
            <PlanBadge />
          </div>
        </header>
      </div>

      <BabySelector isOpen={switcherOpen} onClose={() => setSwitcherOpen(false)} />

      <main className="max-w-2xl mx-auto px-4 py-4" aria-label="Main content">
        <Outlet />
      </main>

      <BottomNav />

      {/* Desktop sidebar */}
      <aside
        className="hidden md:flex fixed left-0 top-0 bottom-0 w-64 bg-white dark:bg-night-surface border-r border-surface-200 dark:border-night-border flex-col"
        aria-label="Sidebar navigation"
      >
        <SidebarContent />
      </aside>
    </div>
  );
}

/**
 * Header identity chip — avatar + name + age. The wordmark it replaces carried
 * no information (the user knows which app they opened) while the baby's name
 * and age never appeared on the dashboard at all.
 *
 * One baby → tap goes to Settings (edit the profile). More than one → tap opens
 * the switcher sheet. No baby yet → "Add baby".
 */
function BabyChip({ babies, selectedBabyId, isFetched, onOpenSwitcher }) {
  const navigate = useNavigate();
  const found = babies.findIndex((b) => b.babyId === selectedBabyId);
  // The selection is repaired asynchronously by useValidatedBabyId; fall back
  // to the first baby so the chip never blinks to "Add baby" mid-repair.
  const index = found >= 0 ? found : babies.length > 0 ? 0 : -1;
  const baby = index >= 0 ? babies[index] : null;
  const multiple = babies.length > 1;

  const chipCls = cx(
    'flex items-center gap-2 min-h-[44px] min-w-0 -ml-1 px-1 py-1 rounded-full text-left',
    'hover:bg-surface-100 active:bg-surface-200 dark:hover:bg-night-card dark:active:bg-night-border',
    'active:scale-[0.98] transition-[background-color,transform] duration-fast ease-out',
    FOCUS_RING
  );

  // Nothing to show until the babies query has resolved once.
  if (!baby && !isFetched) return <span className="min-h-[44px]" aria-hidden="true" />;

  if (!baby) {
    return (
      <button type="button" className={chipCls} onClick={() => navigate('/baby-setup')}>
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300 flex-shrink-0">
          <MdAdd className="w-5 h-5" aria-hidden="true" />
        </span>
        <span className="text-sm font-semibold text-ink dark:text-night-text">Add baby</span>
      </button>
    );
  }

  const age = getBabyAgeText(baby.dateOfBirth);
  const label = multiple
    ? `${baby.name}${age ? `, ${age}` : ''}. Switch baby`
    : `${baby.name}${age ? `, ${age}` : ''}. Open settings`;

  return (
    <button
      type="button"
      className={chipCls}
      onClick={multiple ? onOpenSwitcher : () => navigate('/settings')}
      aria-label={label}
      aria-haspopup={multiple ? 'dialog' : undefined}
    >
      <Avatar name={baby.name} index={index} size="md" />
      <span className="min-w-0 leading-tight">
        <span className="block text-sm font-semibold text-ink dark:text-night-text truncate">
          {baby.name}
        </span>
        {age && (
          <span className="block text-xs text-ink-subtle dark:text-night-muted truncate">{age}</span>
        )}
      </span>
      {multiple && (
        <MdExpandMore
          className="w-5 h-5 flex-shrink-0 text-ink-faint dark:text-night-muted"
          aria-hidden="true"
        />
      )}
    </button>
  );
}

function SidebarContent() {
  return (
    <div className="flex flex-col h-full pt-16">
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto" aria-label="Main navigation">
        {SIDEBAR_LINKS.map((link) => (
          <NavLink
            key={link.to}
            to={link.to}
            end={link.to === '/'}
            className={({ isActive }) =>
              cx(
                'flex items-center gap-3 min-h-[44px] px-3 py-2 rounded-lg',
                'transition-[background-color,color] duration-fast ease-out',
                FOCUS_RING,
                isActive
                  ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300'
                  : 'text-ink-muted dark:text-night-text hover:bg-surface-100 dark:hover:bg-night-card'
              )
            }
          >
            <link.icon className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
            <span className="text-sm font-medium">{link.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
